import { useEffect, useState } from "react";
import { DataTable } from "../components/DataTable";
import { faqService } from "../services/FAQService";
import { categoryService } from "../services/categoryService";
import {
  FAQ,
  CreateFAQRequest,
  UpdateFAQRequest
} from "../interfaces/FAQ";
import {
  Category,
} from "../interfaces/Category";
// ─── Types ───────────────────────────────
type FaqCategory = "General" | "Orders" | "Jewellery" | "Shipping" | "Returns" | "Custom" | "Payment";

type Faq = {
  id: string;
  question: string;
  answer: string;

  category: string;
  faqCategoryID: number;

  status: "Active" | "Hidden";
  order: number;
  createdAt: string;
};

const FAQ_CATEGORIES: FaqCategory[] = ["General", "Orders", "Jewellery", "Shipping", "Returns", "Custom", "Payment"];

const CATEGORY_COLORS: Record<FaqCategory, string> = {
  General: "text-bg-secondary",
  Orders: "text-bg-primary",
  Jewellery: "text-bg-warning",
  Shipping: "text-bg-info",
  Returns: "text-bg-danger",
  Custom: "text-bg-success",
  Payment: "text-bg-dark",
};



// ─── FAQ Editor Panel ─────────────────────
function FaqPanel({
  faq,
  onClose,
  onSave,
  onDelete,
  categories,
  existingFaqs,
}: {
  faq: Partial<Faq>;
  onClose: () => void;
  onSave: (f: Faq) => void;
  onDelete?: (id: string) => void;
  categories: Category[];
  existingFaqs: Faq[];
}) {
  const isNew = !faq.id;
  const [form, setForm] = useState<Faq>({
    id: faq.id ?? `f-${Date.now()}`,
    question: faq.question ?? "",
    answer: faq.answer ?? "",
    // category: faq.category ?? "General",
    category: faq.category ?? "",
    faqCategoryID: faq.faqCategoryID ?? 0,
    status: faq.status ?? "Active",
    order: faq.order ?? 99,
    createdAt: faq.createdAt ?? new Date().toLocaleDateString("en-IN"),
  });
  const [saved, setSaved] = useState(false);
  const [infoModal, setInfoModal] = useState<{
    msg: string;
    type: "success" | "error" | "warn";
  } | null>(null);
  function set<K extends keyof Faq>(k: K, v: Faq[K]) {
    setForm(f => ({ ...f, [k]: v }));
  }
  function validateFaq(form: Faq, existingFaqs: Faq[]) {
    // empty checks
    if (!form.question.trim()) {
      return { ok: false, msg: "Question is required" };
    }

    if (!form.answer.trim()) {
      return { ok: false, msg: "Answer is required" };
    }

    // duplicate question check (case-insensitive)
    const isDuplicate = existingFaqs.some(
      f =>
        f.question.trim().toLowerCase() ===
        form.question.trim().toLowerCase() &&
        f.id !== form.id // allow same record during edit
    );

    if (isDuplicate) {
      return { ok: false, msg: "This question already exists" };
    }

    return { ok: true, msg: "" };
  }
  function handleSave() {

    const result = validateFaq(form, existingFaqs);

    if (!result.ok) {
      setInfoModal({
        msg: result.msg,
        type: "warn",
      });
      return;
    }


    onSave(form);

    setSaved(true);

    setInfoModal({
      msg: "FAQ saved successfully",
      type: "success"
    });

    setTimeout(() => {

      setSaved(false);

    }, 2000);
  }

  return (
    <div className="mty-panel-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="mty-panel">
        <div className="mty-panel-header">
          <div className="d-flex align-items-center gap-2">
            <div className="mst-icon-box"><i className="bi bi-patch-question"></i></div>
            <div>
              <div className="fw-bold mst-title">{isNew ? "Add FAQ" : "Edit FAQ"}</div>
              <small className="mst-subtitle">{isNew ? "Create a new FAQ entry" : form.question.slice(0, 40) + "..."}</small>
            </div>
          </div>
          <button type="button" className="btn-close" onClick={onClose} />
        </div>

        <div className="mty-panel-body">
          {saved && (
            <div className="alert alert-success d-flex align-items-center gap-2 py-2 mb-3 mst-alert">
              <i className="bi bi-check-circle-fill"></i> FAQ saved!
            </div>
          )}

          <div className="mty-section-label"><i className="bi bi-chat-left-text me-2"></i>Question &amp; Answer</div>
          <div className="row g-3 mb-4">
            <div className="col-12">
              <label className="form-label fw-semibold mst-label">Question <span className="text-danger">*</span></label>
              <input className="form-control mst-control"
                placeholder="e.g. What materials are used in your jewellery?"
                value={form.question} onChange={e => set("question", e.target.value)} />
            </div>
            <div className="col-12">
              <label className="form-label fw-semibold mst-label">Answer <span className="text-danger">*</span></label>
              <textarea className="form-control mst-textarea" rows={6}
                placeholder="Write a clear, helpful answer..."
                value={form.answer} onChange={e => set("answer", e.target.value)} />
              <small className="text-muted mt-1 d-block">
                <i className="bi bi-info-circle me-1"></i>
                {form.answer.length} characters
              </small>
            </div>
          </div>

          <div className="mty-section-label"><i className="bi bi-sliders me-2"></i>Settings</div>
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label fw-semibold mst-label">Category</label>
              <select
                className="form-select mst-select"
                value={form.category}
                // onChange={e => set("category", e.target.value as FaqCategory)}
                onChange={e => {

                  const selected =
                    categories.find(
                      x =>
                        x.categoryName ===
                        e.target.value
                    );

                  set("category", e.target.value);

                  set(
                    "faqCategoryID",
                    selected?.categoryID || 0
                  );
                }}
              >
                <option value="">Select Category</option>

                {categories.map(category => (
                  <option
                    key={category.categoryID}
                    value={category.categoryName}
                  >
                    {category.categoryName}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label fw-semibold mst-label">Status</label>
              <select className="form-select mst-select" value={form.status}
                onChange={e => set("status", e.target.value as "Active" | "Hidden")}>
                <option value="Active">Active</option>
                <option value="Hidden">Hidden</option>
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label fw-semibold mst-label">Display Order</label>
              <input type="number" className="form-control mst-control" min={1}
                value={form.order} onChange={e => set("order", Number(e.target.value))} />
            </div>
          </div>
        </div>

        <div className="mty-panel-footer">
          {!isNew && onDelete ? (
            <button type="button" className="btn btn-danger btn-sm mst-btn-delete"
              onClick={() => { onDelete(form.id); onClose(); }}>
              <i className="bi bi-trash me-1"></i>Delete
            </button>
          ) : <div />}
          <div className="d-flex gap-2">
            <button type="button" className="btn mst-btn-reset" onClick={onClose}>Cancel</button>
            <button type="button" className="btn btn-primary mst-btn-save"
              onClick={handleSave}
              disabled={!form.question.trim() || !form.answer.trim()}>
              <i className="bi bi-check-circle me-2"></i>{isNew ? "Add FAQ" : "Save Changes"}
            </button>
          </div>
        </div>
      </div>
      {/* INFO MODAL */}
      {infoModal && (

        <div
          className="pm-del-overlay"
          onClick={() => setInfoModal(null)}
        >

          <div className="pm-del-box">

            <div className="pm-del-title">
              {infoModal.msg}
            </div>

            <div className="pm-del-btns">

              <button
                className="pm-btn-ghost"
                onClick={() => setInfoModal(null)}
                type="button"
              >
                OK
              </button>

            </div>

          </div>

        </div>

      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────
export function FaqPage() {
  // const [faqs, setFaqs]       = useState<Faq[]>(SEED);
  const [faqs, setFaqs] =
    useState<Faq[]>([]);

  const [editing, setEditing] = useState<Partial<Faq> | null>(null);
 const [categories, setCategories] =
  useState<Category[]>([]);

// Keep API order exactly as received
const displayFaqs = faqs;
  const [deleteConfirmId, setDeleteConfirmId] =
    useState<string | null>(null);

  const [infoModal, setInfoModal] = useState<{
    msg: string;
    type: "success" | "error" | "warn";
  } | null>(null);
  useEffect(() => {

    loadCategories();
  }, []);
  useEffect(() => {

    if (categories.length > 0) {

      loadFAQs();

    }

  }, [categories]);


  async function handleSave(faq: Faq) {

    try {

      // =========================
      // CREATE
      // =========================
      if (!faqs.find(f => f.id === faq.id)) {

        const payload: CreateFAQRequest = {

          question: faq.question,

          answer: faq.answer,

          faqCategoryID:
            faq.faqCategoryID,

          displayOrder:
            faq.order,

          status:
            faq.status === "Active"
              ? "active"
              : "inactive",

          isActive:
            faq.status === "Active",

          // SEO
          metaTitle: "",
          metaDescription: "",
          metaKeywords: "",
          canonicalUrl: "",

          // OG
          ogTitle: "",
          ogDescription: "",
          ogImage: "",
          schemaMarkup: ""
        };

        const response =
          await faqService.create(payload);

        // console.log(
        //   "FAQ Created:",
        //   response
        // );

        setFaqs(prev => [
          ...prev,
          faq
        ]);
      }

      // =========================
      // UPDATE
      // =========================
      else {

        const payload: UpdateFAQRequest = {

          faqID:
            Number(
              faq.id.replace("f-", "")
            ),

          question:
            faq.question,

          answer:
            faq.answer,

          faqCategoryID:
            faq.faqCategoryID,

          displayOrder:
            faq.order,

          status:
            faq.status === "Active"
              ? "active"
              : "inactive",

          isActive:
            faq.status === "Active",

          // SEO
          metaTitle: "",
          metaDescription: "",
          metaKeywords: "",
          canonicalUrl: "",

          // OG
          ogTitle: "",
          ogDescription: "",
          ogImage: "",
          schemaMarkup: ""
        };

        const response =
          await faqService.update(payload);

        console.log(
          "FAQ Updated:",
          response
        );

        // setFaqs(prev =>
        //   prev.map(f =>
        //     f.id === faq.id
        //       ? faq
        //       : f
        //   )
        // );
        setFaqs(prev =>
  prev.map(f => {
    if (f.id !== faq.id) return f;

    const selectedCategory = categories.find(
      c => c.categoryID === faq.faqCategoryID
    );

    return {
      ...faq,
      category: selectedCategory?.categoryName || ""
    };
  })
);
      }

      setEditing(null);

    } catch (error) {

      console.error(
        "Error saving FAQ:",
        error
      );
    }
  }
  async function loadFAQs() {

    try {

      const response =
        await faqService.getActiveAll();

      console.log(
        "FAQ API:",
        response
      );

      // =========================
      // MAPPING
      // =========================
      const mappedFaqs: Faq[] =

        response.map((item: any) => {

          const selectedCategory =

            categories.find(
              c =>
                c.categoryID ===
                item.faqCategoryID
            );

          return {

            id:
              `f-${item.faqid}`,

            question:
              item.question || "",

            answer:
              item.answer || "",

            faqCategoryID:
              item.faqCategoryID || 0,

            // CATEGORY NAME
            category:
              selectedCategory?.categoryName || "",

            status:
              item.status === "active"
                ? "Active"
                : "Hidden",

            order:
              item.displayOrder || 0,

            createdAt:
              item.createdDate
                ? new Date(item.createdDate)
                  .toLocaleDateString(
                    "en-IN",
                    {
                      day: "2-digit",
                      month: "short",
                      year: "numeric"
                    }
                  )
                : "-"
          };
        });

      console.log(
        "Mapped FAQs:",
        mappedFaqs
      );

      // =========================
      // SET STATE
      // =========================
      setFaqs(mappedFaqs);

    } catch (error) {

      console.error(error);
    }
  }
  async function loadCategories() {

    try {

      const data =
        await categoryService.getActiveAll();

      
       setCategories(data);

    } catch (error) {

      console.error(error);
    }
  }
  async function handleDelete() {

    if (!deleteConfirmId) {
      return;
    }

    try {

      // API DELETE
      await faqService.delete(
        Number(
          deleteConfirmId.replace("f-", "")
        )
      );

      // REMOVE FROM STATE
      setFaqs(prev =>
        prev.filter(
          f => f.id !== deleteConfirmId
        )
      );

      setInfoModal({
        msg: "FAQ deleted successfully",
        type: "success"
      });

      setDeleteConfirmId(null);

    } catch (error) {

      console.error(error);

      setInfoModal({
        msg: "Error deleting FAQ",
        type: "error"
      });
    }
  }

  async function toggleStatus(id: string) {
    try {
      const faq = faqs.find(f => f.id === id);
      if (!faq) return;

      const payload: UpdateFAQRequest = {
        faqID: Number(id.replace("f-", "")),
        question: faq.question,
        answer: faq.answer,
        faqCategoryID: faq.faqCategoryID,
        displayOrder: faq.order,
        status: faq.status === "Active" ? "inactive" : "active",
        isActive: faq.status !== "Active",
        metaTitle: "",
        metaDescription: "",
        metaKeywords: "",
        canonicalUrl: "",
        ogTitle: "",
        ogDescription: "",
        ogImage: "",
        schemaMarkup: ""
      };

      await faqService.update(payload);

      setFaqs(prev =>
        prev.map(f =>
          f.id === id
            ? { ...f, status: f.status === "Active" ? "Hidden" : "Active" }
            : f
        )
      );

    } catch (err) {
      console.error(err);
    }
  }

 

  return (
    <div className="d-grid gap-4">

      {/* Header Card */}
      <div className="card border-0 shadow-sm mst-card">
        <div className="card-body p-4">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
            <div className="d-flex align-items-center gap-2">
              <div className="mst-icon-box"><i className="bi bi-patch-question"></i></div>
              <div>
                <h5 className="mb-0 fw-bold mst-title">FAQ Manager</h5>
                <small className="mst-subtitle">Manage frequently asked questions shown on the website</small>
              </div>
            </div>
            <button type="button" className="btn btn-primary mst-btn-save"
              onClick={() => setEditing({})}>
              <i className="bi bi-plus-circle me-2"></i>Add FAQ
            </button>
          </div>

          {/* Category pills */}
          
        </div>
      </div>

      {/* Accordion Preview */}
      <div className="card border-0 shadow-sm mst-card">
        <div className="card-body p-4">
          <div className="d-flex align-items-center gap-2 mb-3">
            <i className="bi bi-eye mst-title"></i>
            <span className="fw-semibold mst-title">Frontend Preview</span>
            <span className="mst-smoke" style={{ fontSize: 12 }}>— how FAQ will appear on the website</span>
          </div>

          <div className="faq-accordion">
            {displayFaqs
  .filter(f => f.status === "Active")
  .map((faq, idx) => (
              <FaqAccordionItem key={faq.id} faq={faq} defaultOpen={idx === 0} />
            ))}
            {displayFaqs.filter(
  f => f.status === "Active"
).length === 0 && (
              <div className="text-center py-4 mst-smoke">No active FAQs</div>
            )}
          </div>
        </div>
      </div>

      {/* DataTable */}
      <DataTable<Faq>
        title="All FAQs"
        rows={displayFaqs}
        rowKey={r => r.id}
        searchPlaceholder="Search questions..."
        exportFileName="faqs"
        columns={[
          {
            key: "question", label: "Question", sortable: true,
            render: r => (
              <button type="button" className="btn btn-link p-0 text-start mty-row-link"
                onClick={() => setEditing(r)}>
                <span className="fw-semibold" style={{ fontSize: 13 }}>{r.question}</span>
                <i className="bi bi-box-arrow-up-right ms-1" style={{ fontSize: 10, opacity: 0.5 }}></i>
              </button>
            ),
          },
          {
            key: "category", label: "Category",
            render: r => (
              <span
                className={`badge ${CATEGORY_COLORS[r.category as FaqCategory] || "text-bg-secondary"
                  }`}
              >
                {r.category}
              </span>
            ),
          },
          {
            key: "answer", label: "Answer",
            render: r => (
              <span className="mst-smoke" style={{ fontSize: 12, maxWidth: 220, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {r.answer}
              </span>
            ),
          },
          { key: "order", label: "Order" },
          {
            key: "status", label: "Status",
            render: r => <span className={`badge ${r.status === "Active" ? "text-bg-success" : "text-bg-secondary"}`}>{r.status}</span>,
          },
          { key: "createdAt", label: "Created", sortable: true },
          {
            key: "actions", label: "Actions", className: "text-end",
            render: r => (
              <div className="pg-actions">
                <button type="button" className="btn btn-sm btn-outline-primary mst-btn-status"
                  onClick={() => setEditing(r)} title="Edit">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                </button>
                <button type="button" className="btn btn-sm btn-outline-secondary mst-btn-status"
                  onClick={() => toggleStatus(r.id)}>
                  {r.status === "Active" ? "Hide" : "Show"}
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger mst-btn-delete"
                  onClick={() =>
                    setDeleteConfirmId(r.id)
                  }
                >
                  <i className="bi bi-trash"></i>
                </button>
              </div>

            ),
          },
        ]}
      />

      {editing !== null && (
        <FaqPanel
          faq={editing}
          onClose={() => setEditing(null)}
          onSave={handleSave}
          onDelete={handleDelete}
          categories={categories}
          existingFaqs={faqs}
        />
      )}
      {/* DELETE CONFIRM MODAL */}
      {deleteConfirmId && (

        <div
          className="pm-del-overlay"
          onClick={(e) => {

            if (e.target === e.currentTarget) {

              setDeleteConfirmId(null);
            }
          }}
        >

          <div className="pm-del-box">

            <div className="pm-del-ico">

              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#ef4444"
                strokeWidth="2.5"
              >
                <polyline points="3 6 5 6 21 6" />

                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />

                <path d="M10 11v6" />

                <path d="M14 11v6" />
              </svg>

            </div>

            <div className="pm-del-title">
              Delete FAQ?
            </div>

            <div className="pm-del-sub">
              This action cannot be undone.
            </div>

            <div className="pm-del-btns">

              <button
                className="pm-btn-ghost"
                onClick={() =>
                  setDeleteConfirmId(null)
                }
                type="button"
              >
                Cancel
              </button>

              <button
                className="pm-btn-danger"
                onClick={handleDelete}
                type="button"
              >
                Delete
              </button>

            </div>

          </div>

        </div>
      )}
      {infoModal && (
        <div className="pm-del-overlay" onClick={() => setInfoModal(null)}>
          <div className="pm-del-box">

            <div className="pm-del-title">
              {infoModal.msg}
            </div>

            <div className="pm-del-btns">
              <button
                className="pm-btn-ghost"
                onClick={() => setInfoModal(null)}
              >
                OK
              </button>
            </div>

          </div>
        </div>
      )}
    </div>

  );
}

// ─── Accordion Item (preview) ─────────────
function FaqAccordionItem({ faq, defaultOpen }: { faq: Faq; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen ?? false);
  return (
    <div className={`faq-item ${open ? "open" : ""}`}>
      <button type="button" className="faq-q" onClick={() => setOpen(o => !o)}>
        <span>{faq.question}</span>
        <i className={`bi ${open ? "bi-dash-circle" : "bi-plus-circle"} faq-icon`}></i>
      </button>
      {open && <div className="faq-a">{faq.answer}
      </div>}

    </div>
  );
}

